import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/adminApi";
import { fmtINR, fmtDate, fmtDateTime, fmtRelative } from "../lib/format";
import { ArrowLeft, Building2, Users, Sparkles, IndianRupee, Ban, CheckCircle2, UserCog, ArrowUpDown, Loader2, Calendar, ShoppingCart, FileText, Clock, Globe, AlertTriangle } from "lucide-react";
import ImpersonateModal from "../components/ImpersonateModal";
import PlanChangeModal from "../components/PlanChangeModal";
import BillingTab from "../components/BillingTab";

const PLAN_BADGE = {
  free: { bg: "bg-slate-100", text: "text-slate-600" },
  trial: { bg: "bg-amber-50", text: "text-amber-700" },
  standard: { bg: "bg-blue-50", text: "text-blue-700" },
  pro: { bg: "bg-violet-50", text: "text-violet-700" },
  premium: { bg: "bg-[#7C5CFF]/10", text: "text-[#7C5CFF]" },
};

export default function TenantDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showImp, setShowImp] = useState(false);
  const [showPlan, setShowPlan] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [regionData, setRegionData] = useState(null);

  const load = React.useCallback(async () => {
    try {
      const res = await api.get(`/tenants/${id}`);
      setData(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [id]);

  // Fetch raw tenant doc (includes country_code etc.) via empty PATCH
  const loadRegion = React.useCallback(async () => {
    try {
      const res = await api.patch(`/tenants/${id}`, {});
      setRegionData({
        country_code: res.data.country_code || "",
        currency_code: res.data.currency_code || "",
        tax_label: res.data.tax_label || "",
        plan: res.data.plan || "",
      });
    } catch { /* silent */ }
  }, [id]);

  useEffect(() => { load(); loadRegion(); }, [load, loadRegion]);

  // Dynamic browser title while a tenant is loaded.
  useEffect(() => {
    if (data?.name) document.title = `1000AD | ${data.name}`;
    return () => { document.title = "1000AD | AI Business Intelligence Manager"; };
  }, [data?.name]);

  const suspendTenant = async () => {
    const reason = window.prompt(`Suspend "${data?.name}"? Enter reason:`);
    if (!reason) return;
    try { await api.post(`/tenants/${id}/suspend`, { reason }); load(); } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  const activateTenant = async () => {
    if (!window.confirm("Activate this tenant?")) return;
    try { await api.post(`/tenants/${id}/activate`); load(); } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><Loader2 className="w-6 h-6 text-[#7C5CFF] animate-spin" /></div>;
  }
  if (!data) return <p className="p-8 text-[#5B617A]">Tenant not found.</p>;

  const pl = PLAN_BADGE[data.effective_plan] || PLAN_BADGE.free;
  const isSuspended = data.status === "suspended";

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto glass-animate" data-testid="tenant-detail-page">
      {/* Header */}
      <div className="flex items-start gap-3 mb-6">
        <button onClick={() => navigate(-1)} data-testid="detail-back" className="mt-1 p-1.5 hover:bg-[#7C5CFF]/[0.06] rounded-xl transition-colors">
          <ArrowLeft className="w-5 h-5 text-[#0B0F1A]" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-extrabold text-[#0B0F1A] tracking-tight truncate" data-testid="tenant-name">{data.name}</h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${pl.bg} ${pl.text}`}>{data.effective_plan}</span>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${isSuspended ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
              {data.status}
            </span>
            {data.state && <span className="text-xs text-[#5B617A]">{data.state}</span>}
          </div>
          {data.owner_email && <p className="text-xs text-[#5B617A] mt-1">Owner: {data.owner_name || "—"} · {data.owner_email}</p>}
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 shrink-0 flex-wrap">
          <ActionBtn icon={ArrowUpDown} label="Change plan" onClick={() => setShowPlan(true)} testId="detail-change-plan" />
          <ActionBtn icon={UserCog} label="Impersonate" onClick={() => setShowImp(true)} testId="detail-impersonate" disabled={isSuspended} />
          {isSuspended ? (
            <ActionBtn icon={CheckCircle2} label="Activate" onClick={activateTenant} testId="detail-activate" success />
          ) : (
            <ActionBtn icon={Ban} label="Suspend" onClick={suspendTenant} testId="detail-suspend" danger />
          )}
        </div>
      </div>

      {isSuspended && data.suspended_reason && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-[20px] p-3 mb-5" data-testid="suspend-reason-banner">
          <Ban className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
          <p className="text-sm text-red-700">Suspended: {data.suspended_reason}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-[#7C5CFF]/[0.08]" data-testid="tenant-tabs">
        {["overview", "region", "billing", "audit"].map((tab) => (
          <button key={tab} data-testid={`tab-${tab}`} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 text-sm font-semibold capitalize transition-colors border-b-2 -mb-px ${activeTab === tab ? "border-[#7C5CFF] text-[#7C5CFF]" : "border-transparent text-[#5B617A] hover:text-[#0B0F1A]"}`}>
            {tab === "region" ? "Region & Plan" : tab}
          </button>
        ))}
      </div>

      {activeTab === "overview" && (
      <>
      {/* Summary stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard icon={Users} label="Members" value={data.active_member_count} />
        <StatCard icon={ShoppingCart} label="Sales (30d)" value={data.sales_count_30d} />
        <StatCard icon={IndianRupee} label="Total Volume" value={fmtINR(data.total_sales_volume)} />
        <StatCard icon={Sparkles} label="AI Calls (30d)" value={data.ai_calls_30d} />
        <StatCard icon={FileText} label="Invoices (30d)" value={data.invoices_count_30d} />
        <StatCard icon={IndianRupee} label="AI Cost (30d)" value={fmtINR(data.ai_cost_inr_30d)} />
        <StatCard icon={Calendar} label="Created" value={fmtDate(data.created_at)} />
        <StatCard icon={Clock} label="Last Activity" value={fmtRelative(data.last_activity_at)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Members list */}
        <div className="glass-card p-5" data-testid="members-section">
          <p className="text-sm font-bold text-[#0B0F1A] mb-3">Team Members ({(data.members || []).length})</p>
          {(data.members || []).length === 0 ? (
            <p className="text-sm text-[#5B617A]">No members.</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-auto">
              {data.members.map((m) => (
                <div key={m.id || m.email} className="flex items-center gap-2.5 py-1.5" data-testid={`member-${m.email}`}>
                  <div className="w-7 h-7 rounded-full bg-[#7C5CFF]/10 flex items-center justify-center text-[10px] font-bold text-[#7C5CFF]">
                    {(m.name || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0B0F1A] truncate">{m.name}</p>
                    <p className="text-[10px] text-[#5B617A] truncate">{m.email} · {m.role}</p>
                  </div>
                  <span className="text-[10px] text-[#5B617A]">{fmtRelative(m.last_login_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent AI events */}
        <div className="glass-card p-5" data-testid="ai-events-section">
          <p className="text-sm font-bold text-[#0B0F1A] mb-3">Recent AI Usage</p>
          {(data.recent_ai_usage_events || []).length === 0 ? (
            <p className="text-sm text-[#5B617A]">No AI usage events.</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-auto">
              {data.recent_ai_usage_events.map((ev, i) => (
                <div key={i} className="flex items-start gap-2 py-1.5 border-b border-[#7C5CFF]/[0.04] last:border-0">
                  <Sparkles className="w-3.5 h-3.5 text-[#7C5CFF] mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-[#0B0F1A] truncate">{ev.tool_name || ev.action || "chat"}</p>
                    <p className="text-[10px] text-[#5B617A]">{ev.tokens_prompt ?? 0}+{ev.tokens_completion ?? 0} tokens · {fmtINR(ev.cost_estimate_inr ?? 0)}</p>
                  </div>
                  <span className="text-[10px] text-[#5B617A] shrink-0">{fmtRelative(ev.timestamp)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      </>
      )}

      {activeTab === "region" && (
        <RegionPlanPanel
          tenantId={id}
          tenantName={data.name}
          regionData={regionData}
          onSaved={() => { load(); loadRegion(); }}
        />
      )}

      {activeTab === "billing" && <BillingTab tenantId={id} />}

      {activeTab === "audit" && (
      <div className="glass-card p-5" data-testid="tenant-audit-section">
        <p className="text-sm font-bold text-[#0B0F1A] mb-3">Admin Audit Trail (this tenant)</p>
          {(data.recent_audit_events || []).length === 0 ? (
            <p className="text-sm text-[#5B617A]">No admin actions on this tenant yet.</p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-auto">
              {data.recent_audit_events.map((ev, i) => (
                <div key={i} className="flex items-start gap-2 py-2 border-b border-[#7C5CFF]/[0.04] last:border-0" data-testid={`audit-ev-${i}`}>
                  <div className="w-1.5 h-1.5 rounded-full bg-[#7C5CFF] mt-1.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#0B0F1A]">
                      <span className="font-semibold">{ev.admin_email}</span>{" "}
                      <span className="bg-[#7C5CFF]/[0.08] text-[#5B617A] text-[10px] px-1.5 py-0.5 rounded font-mono">{ev.action}</span>
                    </p>
                    {ev.after && <p className="text-[10px] text-[#5B617A] mt-0.5 truncate">{JSON.stringify(ev.after)}</p>}
                  </div>
                  <span className="text-[10px] text-[#5B617A] shrink-0">{fmtDateTime(ev.timestamp)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showImp && <ImpersonateModal tenant={{ tenant_id: id, name: data.name }} onClose={() => setShowImp(false)} />}
      {showPlan && <PlanChangeModal tenant={{ tenant_id: id, name: data.name, effective_plan: data.effective_plan }} onClose={() => { setShowPlan(false); load(); }} />}
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="glass-card p-3">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5 text-[#7C5CFF]" />
        <span className="text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-lg font-bold text-[#0B0F1A]" style={{ fontVariantNumeric: "tabular-nums" }}>{value}</p>
    </div>
  );
}

function ActionBtn({ icon: Icon, label, onClick, testId, danger, success, disabled }) {
  const base = danger
    ? "border-red-200 text-red-600 hover:bg-red-50"
    : success
      ? "border-emerald-200 text-emerald-600 hover:bg-emerald-50"
      : "border-[#7C5CFF]/[0.12] text-[#0B0F1A] hover:bg-[#7C5CFF]/[0.06]";
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-semibold transition-colors disabled:opacity-40 ${base}`}
    >
      <Icon className="w-3.5 h-3.5" />{label}
    </button>
  );
}

const COUNTRIES = [
  { code: "", label: "Not set (defaults to IN)" },
  { code: "IN", label: "🇮🇳 India" },
  { code: "AE", label: "🇦🇪 United Arab Emirates" },
  { code: "SA", label: "🇸🇦 Saudi Arabia" },
  { code: "QA", label: "🇶🇦 Qatar" },
  { code: "BH", label: "🇧🇭 Bahrain" },
  { code: "KW", label: "🇰🇼 Kuwait" },
  { code: "OM", label: "🇴🇲 Oman" },
];
const CURRENCIES = ["INR", "AED", "SAR", "QAR", "OMR", "BHD", "KWD", "USD"];

function RegionPlanPanel({ tenantId, tenantName, regionData, onSaved }) {
  const [form, setForm] = useState({
    country_code: "",
    currency_code: "",
    tax_label: "",
    plan_slug: "",
  });
  const [origCountry, setOrigCountry] = useState("");
  const [saving, setSaving] = useState(false);
  const [packages, setPackages] = useState([]);
  const [saveMsg, setSaveMsg] = useState(null);

  useEffect(() => {
    if (regionData) {
      setForm({
        country_code: regionData.country_code || "",
        currency_code: regionData.currency_code || "",
        tax_label: regionData.tax_label || "",
        plan_slug: "",
      });
      setOrigCountry(regionData.country_code || "");
    }
  }, [regionData]);

  useEffect(() => {
    api.get("/packages").then(r => {
      setPackages((r.data.items || []).map(p => ({ slug: p.slug, name: p.name || p.slug })));
    }).catch(() => {});
  }, []);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const countryChanged = form.country_code !== origCountry && form.country_code !== "";

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      const patch = {};
      if (form.country_code) patch.country_code = form.country_code;
      if (form.currency_code) patch.currency_code = form.currency_code;
      if (form.tax_label) patch.tax_label = form.tax_label;
      if (form.plan_slug) patch.plan_slug = form.plan_slug;
      if (Object.keys(patch).length === 0) {
        setSaveMsg({ type: "warn", text: "No changes to save." });
        setSaving(false);
        return;
      }
      await api.patch(`/tenants/${tenantId}`, patch);
      setSaveMsg({ type: "ok", text: "Region & plan updated successfully." });
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? d.map(x => x.msg || JSON.stringify(x)).join("; ") : "Save failed";
      setSaveMsg({ type: "err", text: msg });
    } finally { setSaving(false); }
  };

  if (!regionData) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#7C5CFF]" /></div>;
  }

  return (
    <div className="glass-card p-5 max-w-lg" data-testid="region-plan-panel">
      <div className="flex items-center gap-2 mb-4">
        <Globe className="w-4 h-4 text-[#7C5CFF]" />
        <p className="text-sm font-bold text-[#0B0F1A]">Region & Plan — {tenantName}</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-[10px] font-bold text-[#5B617A] uppercase tracking-wider mb-1">Country</label>
          <select
            value={form.country_code}
            onChange={e => set("country_code", e.target.value)}
            className="input-field w-full"
            data-testid="region-country-select"
          >
            {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        </div>

        {countryChanged && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="country-changed-warning">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-800 font-medium">Country changed — review currency manually</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] font-bold text-[#5B617A] uppercase tracking-wider mb-1">Currency</label>
            <select
              value={form.currency_code}
              onChange={e => set("currency_code", e.target.value)}
              className="input-field w-full"
              data-testid="region-currency-select"
            >
              <option value="">Not set</option>
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-[#5B617A] uppercase tracking-wider mb-1">Tax Label</label>
            <input
              value={form.tax_label}
              onChange={e => set("tax_label", e.target.value)}
              placeholder="e.g. GST, VAT"
              className="input-field w-full"
              data-testid="region-tax-label-input"
            />
          </div>
        </div>

        <div>
          <label className="block text-[10px] font-bold text-[#5B617A] uppercase tracking-wider mb-1">Plan Override</label>
          <select
            value={form.plan_slug}
            onChange={e => set("plan_slug", e.target.value)}
            className="input-field w-full"
            data-testid="region-plan-select"
          >
            <option value="">— no change —</option>
            {packages.map(p => <option key={p.slug} value={p.slug}>{p.name}</option>)}
          </select>
          <p className="text-[10px] text-[#5B617A] mt-1">Select a plan to override the tenant's current plan.</p>
        </div>

        {saveMsg && (
          <div className={`rounded-xl px-3 py-2 text-xs font-medium ${
            saveMsg.type === "ok" ? "bg-emerald-50 text-emerald-700" :
            saveMsg.type === "warn" ? "bg-amber-50 text-amber-700" :
            "bg-red-50 text-red-700"
          }`} data-testid="region-save-msg">
            {saveMsg.text}
          </div>
        )}

        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="region-save-btn"
          className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#7C5CFF] hover:bg-[#6A4DE0] disabled:opacity-50 transition-colors"
        >
          {saving ? <Loader2 size={14} className="animate-spin mx-auto" /> : "Save Region & Plan"}
        </button>
      </div>
    </div>
  );
}
